import {useMutation, useQueryClient} from '@github-ui/react-query'
import {PageData} from '@github-ui/pull-request-page-data-tooling/page-data'
import {usePageDataUrl} from '@github-ui/pull-request-page-data-tooling/use-page-data-url'
import type {HeaderPageData} from '../page-data/payloads/header'
import {useHeaderPageDataQueryKey} from '../page-data/loaders/use-header-page-data'
import {
  fetchWithErrorHandling,
  parseJSONWithBetterErrors,
  throwErrorsIfBadResponse,
} from '@github-ui/pull-request-page-data-tooling/fetch-error-handling'

type UpdateTitleResponse = {
  pullRequest: Pick<HeaderPageData['pullRequest'], 'title' | 'titleHtml'>
}

const UPDATE_TITLE_TIMEOUT_ERROR = 'Updating the pull request title timed out. Please try again.'
const UPDATE_TITLE_TIMEOUT_ERROR_CODE = 'update_title_timeout'
const MIN_RETRY_DELAY_MS = 250
const RETRY_JITTER_MS = 250

class RetryableTitleUpdateTimeoutError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'RetryableTitleUpdateTimeoutError'
  }
}

export function updateTitleRetryDelay() {
  return MIN_RETRY_DELAY_MS + Math.random() * RETRY_JITTER_MS
}

async function parseUpdateTitleTimeoutError(response: Response): Promise<Error> {
  try {
    const responseBody: unknown = await response.json()
    if (!responseBody || typeof responseBody !== 'object') return new Error(UPDATE_TITLE_TIMEOUT_ERROR)

    const body = responseBody as Record<string, unknown>
    const errorMessage = typeof body.error === 'string' ? body.error : UPDATE_TITLE_TIMEOUT_ERROR
    return body.errorCode === UPDATE_TITLE_TIMEOUT_ERROR_CODE
      ? new RetryableTitleUpdateTimeoutError(errorMessage)
      : new Error(errorMessage)
  } catch {
    return new Error(UPDATE_TITLE_TIMEOUT_ERROR)
  }
}

/**
 * Updates the title of a pull request. On success, will update Tanstack query data.
 *
 * To run additional callbacks, you can pass any supported callback option to the `mutate` function when it's
 * called, including `onSuccess`, `onError`, and `onSettled`. These additional callbacks will run after the
 * callbacks defined in the hook below.
 *
 * For more details, see https://tanstack.com/query/latest/docs/framework/react/guides/mutations#mutation-side-effects
 *
 * @example
 * ```ts
 * const {mutate} = useUpdateTitleMutation()
 *
 * // Required arguments
 * const mutationArguments = {id: 1, title: 'very cool title', expectedTitle: 'old title'}
 *
 * // Optional callbacks, will run after callbacks defined in useUpdateTitleMutation hook
 * const mutationCallbacks = {
 *   onError: error => console.log(error.message),
 *   onSuccess: (data) => console.log(data),
 * }
 *
 * mutate(mutationArguments, mutationCallbacks)
 * ```
 */
export function useUpdateTitleMutation() {
  const queryClient = useQueryClient()
  const titleUrl = usePageDataUrl(PageData.updateTitle)
  const headerPageDataQueryKey = useHeaderPageDataQueryKey()

  return useMutation({
    networkMode: 'always',
    retry: (failureCount, error) => failureCount < 1 && error instanceof RetryableTitleUpdateTimeoutError,
    retryDelay: updateTitleRetryDelay,
    mutationFn: async ({id, title, expectedTitle}: {id: number; title: string; expectedTitle: string}) => {
      const result = await fetchWithErrorHandling(titleUrl, {method: 'PATCH', body: {id, title, expectedTitle}})

      if (result.status === 504) {
        throw await parseUpdateTitleTimeoutError(result)
      }

      const responseBody = await parseJSONWithBetterErrors(result)
      throwErrorsIfBadResponse(result, responseBody)
      return responseBody as UpdateTitleResponse
    },
    onSuccess: responseBody => {
      queryClient.setQueryData(headerPageDataQueryKey, (old: HeaderPageData | undefined) => {
        if (!old) return old
        return {
          ...old,
          pullRequest: {
            ...old.pullRequest,
            title: responseBody.pullRequest.title,
            titleHtml: responseBody.pullRequest.titleHtml,
          },
        }
      })
    },
  })
}
